// client/src/lib/indexedDB.ts
interface OfflineCredentials {
  userId: string;
  email: string;
  passwordHash: string;
  salt: string;
  lastLogin: number;
  expiresAt: number;
}

interface CachedTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
}

interface OfflineData {
  userProfile: any;
  farms: any[];
  cropRecords: any[];
  weatherHistory: any[];
  pestGuides: any[];
  syncQueue: any[];
  lastSync: number;
}

class IndexedDBService {
  private dbName = 'AgroSenseOffline';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    if (this.db) return; // Already initialized
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('IndexedDB upgrade needed, creating stores');
        
        // Credentials store
        if (!db.objectStoreNames.contains('credentials')) {
          const credStore = db.createObjectStore('credentials', { keyPath: 'userId' });
          console.log('Created credentials store');
        }
        
        // Tokens store
        if (!db.objectStoreNames.contains('tokens')) {
          const tokenStore = db.createObjectStore('tokens', { keyPath: 'userId' });
          console.log('Created tokens store');
        }
        
        // Offline data store
        if (!db.objectStoreNames.contains('offlineData')) {
          const dataStore = db.createObjectStore('offlineData', { keyPath: 'userId' });
          console.log('Created offlineData store');
        }
      };
    });
  }

  async storeCredentials(credentials: OfflineCredentials): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['credentials'], 'readwrite');
    const store = transaction.objectStore('credentials');
    
    return new Promise((resolve, reject) => {
      const request = store.put(credentials);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCredentials(userId: string): Promise<OfflineCredentials | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['credentials'], 'readonly');
    const store = transaction.objectStore('credentials');
    
    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async storeTokens(tokens: CachedTokens): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['tokens'], 'readwrite');
    const store = transaction.objectStore('tokens');
    
    return new Promise((resolve, reject) => {
      const request = store.put(tokens);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getTokens(userId: string): Promise<CachedTokens | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['tokens'], 'readonly');
    const store = transaction.objectStore('tokens');
    
    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async storeOfflineData(userId: string, data: Partial<OfflineData>): Promise<void> {
    if (!this.db) await this.init();
    
    const existing = await this.getOfflineData(userId) || { userId };
    const updated = { ...existing, ...data, userId };
    
    const transaction = this.db!.transaction(['offlineData'], 'readwrite');
    const store = transaction.objectStore('offlineData');
    
    return new Promise((resolve, reject) => {
      const request = store.put(updated);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineData(userId: string): Promise<OfflineData | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['offlineData'], 'readonly');
    const store = transaction.objectStore('offlineData');
    
    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async clearUserData(userId: string): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['credentials', 'tokens', 'offlineData'], 'readwrite');
    
    await Promise.all([
      new Promise<void>((resolve, reject) => {
        const request = transaction.objectStore('credentials').delete(userId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise<void>((resolve, reject) => {
        const request = transaction.objectStore('tokens').delete(userId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }),
      new Promise<void>((resolve, reject) => {
        const request = transaction.objectStore('offlineData').delete(userId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ]);
  }
}

export const indexedDBService = new IndexedDBService();